import { useCallback, useEffect, useRef, useState } from 'react';

import { StoryAnimation } from '../../../enums';
import { useIncrementInterval } from '../../../hooks/useIncrementInterval';
import { useInIframe } from '../../../hooks/useInIframe';
import { useScrollArray } from '../../../hooks/useScrollArray';
import {
  InternalMessageType,
  isInternaldMessageEvent,
  sendMessageToEmbed,
} from '../../../lib/internal-messages';
import { storyAnimationsImplyVideo } from '../../../utils';
import { GalleryVisibilityState } from '../enums';

interface UseGalleryAnimationProps {
  id: string;
  galleryAnimations: StoryAnimation[];
  numberOfStories: number;
}

export const useGalleryAnimation = ({
  id,
  galleryAnimations,
  numberOfStories,
}: UseGalleryAnimationProps) => {
  const [animatedStoryIndex, setAnimatedStoryIndex] = useState(() => {
    const requiresActiveStory =
      galleryAnimations.includes(StoryAnimation.Autoplay) ||
      galleryAnimations.includes(StoryAnimation.Pulse);
    return requiresActiveStory ? 0 : -1;
  });
  const isPreloaded = useRef<boolean>(false);

  const { elementRefs, scrollIntoView, setElementRef } = useScrollArray();

  const { start: startAutoplay, stop: stopAutoplay } = useIncrementInterval({
    value: animatedStoryIndex,
    maxValue: numberOfStories,
    onChange: (value: number) => {
      setAnimatedStoryIndex(value);
      scrollIntoView(value);
    },
  });

  const isInIframe = useInIframe();

  const grabAnimation = useCallback(
    (index: number) => {
      setAnimatedStoryIndex(index);
      stopAutoplay();
    },
    [setAnimatedStoryIndex, stopAutoplay]
  );

  const restoreAnimation = useCallback(() => {
    if (galleryAnimations.includes(StoryAnimation.Autoplay)) {
      startAutoplay();
    } else {
      stopAutoplay(); // Redundant, but just in case

      if (galleryAnimations.includes(StoryAnimation.Pulse)) {
        setAnimatedStoryIndex(0);
      } else {
        setAnimatedStoryIndex(-1);
      }
    }
  }, [galleryAnimations, startAutoplay, stopAutoplay]);

  const preloadVideos = useCallback(() => {
    let anyVideoPreloaded = false;
    elementRefs.current.forEach((s) => {
      if (!s.current) return;
      const videoElement = s.current.querySelector('video');
      if (videoElement && videoElement.getAttribute('preload') !== 'auto') {
        videoElement.setAttribute('preload', 'auto');
        anyVideoPreloaded = true;
      }
    });
    if (anyVideoPreloaded) isPreloaded.current = true;
  }, [elementRefs]);

  const initializeAnimations = useCallback(() => {
    if (storyAnimationsImplyVideo(galleryAnimations)) {
      preloadVideos();
    }
  }, [galleryAnimations, preloadVideos]);

  const startAnimations = useCallback(() => {
    if (galleryAnimations.includes(StoryAnimation.Autoplay)) {
      startAutoplay();
    }
  }, [galleryAnimations, startAutoplay]);

  const stopAnimations = useCallback(() => {
    if (galleryAnimations.includes(StoryAnimation.Autoplay)) {
      stopAutoplay();
    }
  }, [galleryAnimations, stopAutoplay]);

  useEffect(() => {
    if (isInIframe === false) {
      initializeAnimations();
      startAnimations();
    }

    const handleMessage = (event: MessageEvent) => {
      if (isInternaldMessageEvent(event)) {
        const { type, payload } = event.data;

        if (type === InternalMessageType.GalleryVisibility) {
          switch (payload.state) {
            case GalleryVisibilityState.BeforeEnter:
            case GalleryVisibilityState.Entering:
              if (!isPreloaded.current) preloadVideos();
              break;

            case GalleryVisibilityState.Visible:
              if (!isPreloaded.current) preloadVideos();
              startAnimations();
              break;

            case GalleryVisibilityState.Exiting:
              stopAnimations();
              break;

            default:
              break;
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    sendMessageToEmbed({
      type: InternalMessageType.GalleryReady,
      payload: { id, value: true },
    });

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [
    id,
    isInIframe,
    initializeAnimations,
    preloadVideos,
    startAnimations,
    stopAnimations,
  ]);

  return {
    animatedStoryIndex,
    grabAnimation,
    restoreAnimation,
    registerStoryRef: setElementRef,
  };
};
